import { useCallback, useEffect, useState } from 'react';
import {
  marketintelApi,
  type CategoryTrend,
  type CompetitiveObservation,
  type MarketDashboard,
  type MarketRecommendation,
  type MarketSignal,
  type OpportunityScore,
  type PortfolioStrategy,
  type ScenarioRecord,
  type TrendRecord,
} from '../../api/marketintel';

type Tab = 'overview' | 'opportunities' | 'trends' | 'categories' | 'competitive' | 'portfolio' | 'recommendations' | 'scenarios';

function directionClass(d: string) {
  if (d === 'accelerating' || d === 'emerging') return 'text-emerald-700 bg-emerald-50';
  if (d === 'declining') return 'text-red-700 bg-red-50';
  return 'text-slate-600 bg-slate-50';
}

function actionClass(a: string) {
  if (a === 'expand' || a === 'new_product_idea' || a === 'category_expansion') return 'text-emerald-700 bg-emerald-50';
  if (a === 'retire') return 'text-red-700 bg-red-50';
  return 'text-blue-700 bg-blue-50';
}

export function MarketIntelPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<MarketDashboard | null>(null);
  const [signals, setSignals] = useState<MarketSignal[]>([]);
  const [categories, setCategories] = useState<CategoryTrend[]>([]);
  const [competitive, setCompetitive] = useState<CompetitiveObservation[]>([]);
  const [opportunities, setOpportunities] = useState<OpportunityScore[]>([]);
  const [trends, setTrends] = useState<TrendRecord[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioStrategy[]>([]);
  const [recommendations, setRecommendations] = useState<MarketRecommendation[]>([]);
  const [scenarios, setScenarios] = useState<ScenarioRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, sig, cats, comp, opps, tr, port, recs, sc] = await Promise.all([
        marketintelApi.dashboard(),
        marketintelApi.listSignals(),
        marketintelApi.listCategories(),
        marketintelApi.listCompetitive(),
        marketintelApi.listOpportunities(),
        marketintelApi.listTrends(),
        marketintelApi.listPortfolio(),
        marketintelApi.listRecommendations(),
        marketintelApi.listScenarios(),
      ]);
      setDashboard(dash);
      setSignals(sig);
      setCategories(cats);
      setCompetitive(comp);
      setOpportunities(opps);
      setTrends(tr);
      setPortfolio(port);
      setRecommendations(recs);
      setScenarios(sc);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load market intelligence');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try { await marketintelApi.generateRecommendations(); await refresh(); }
    finally { setBusy(false); }
  };

  const runScenario = async (type: string, name: string) => {
    setBusy(true);
    try { await marketintelApi.runScenario({ name, type }); await refresh(); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Market Dashboard' },
    { id: 'opportunities', label: 'Opportunity Explorer' },
    { id: 'trends', label: 'Trend Center' },
    { id: 'categories', label: 'Category Analyzer' },
    { id: 'competitive', label: 'Competitive Insights' },
    { id: 'portfolio', label: 'Portfolio Strategy' },
    { id: 'recommendations', label: 'Recommendation Center' },
    { id: 'scenarios', label: 'Scenario Simulator' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Enterprise Market Intelligence</h1>
          <p className="text-sm text-slate-500">Competitor analysis, opportunity discovery &amp; AI market copilot — BCP-00005</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-rose-100 text-rose-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Categories Tracked', value: dashboard.categories_tracked.toString() },
              { label: 'Competitors Observed', value: dashboard.competitors_observed.toString() },
              { label: 'Active Opportunities', value: dashboard.active_opportunities.toString() },
              { label: 'Emerging Trends', value: dashboard.emerging_trends.toString() },
              { label: 'Avg Category Growth', value: `${dashboard.avg_category_growth_pct.toFixed(1)}%` },
              { label: 'Competitive Pressure', value: dashboard.avg_competitive_pressure.toFixed(0) },
              { label: 'Top Opportunity', value: dashboard.top_opportunity_score.toFixed(0) },
              { label: 'Market Readiness', value: dashboard.market_readiness_avg.toFixed(0) },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-slate-200 px-4 py-3"><h2 className="font-medium text-slate-900">Market Signals</h2></div>
            <div className="divide-y divide-slate-100">
              {signals.map((s) => (
                <div key={s.id} className="px-4 py-3 flex justify-between gap-4">
                  <div>
                    <span className="text-xs text-rose-600 capitalize">{s.signal_type.replace(/_/g, ' ')}</span>
                    <p className="font-medium text-slate-900">{s.title}</p>
                    {s.description && <p className="text-sm text-slate-500">{s.description}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-lg font-semibold text-slate-900">{s.strength.toFixed(0)}</p>
                    <p className="text-xs text-slate-400">strength</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'opportunities' && (
        <div className="space-y-4">
          {opportunities.map((o) => (
            <div key={o.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <span className="text-xs font-medium text-rose-600 capitalize">{o.type.replace(/_/g, ' ')}</span>
                  <h3 className="font-medium text-slate-900">{o.title}</h3>
                  {o.evidence && <p className="mt-1 text-sm text-slate-600">{o.evidence}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-2xl font-bold text-rose-600">{o.composite_score.toFixed(0)}</p>
                  <p className="text-xs text-slate-500">composite score</p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
                <span>Category: {o.category_opportunity_score.toFixed(0)}</span>
                <span>Product: {o.product_opportunity_score.toFixed(0)}</span>
                <span>Pressure: {o.competitive_pressure_score.toFixed(0)}</span>
                <span>Growth: {o.growth_potential_score.toFixed(0)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'trends' && (
        <div className="space-y-3">
          {trends.map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start">
                <div>
                  <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${directionClass(t.direction)}`}>{t.direction}</span>
                  <h3 className="font-medium text-slate-900 mt-1">{t.name}</h3>
                  {t.description && <p className="text-sm text-slate-600">{t.description}</p>}
                </div>
                <p className="text-lg font-semibold">{t.strength.toFixed(0)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'categories' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Growth</th>
                <th className="px-4 py-2">Density</th>
                <th className="px-4 py-2">Content Quality</th>
                <th className="px-4 py-2">Direction</th>
                <th className="px-4 py-2">Niche</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {categories.map((c) => (
                <tr key={c.id}>
                  <td className="px-4 py-2 font-medium">{c.category}</td>
                  <td className="px-4 py-2">{c.growth_pct > 0 ? '+' : ''}{c.growth_pct.toFixed(1)}%</td>
                  <td className="px-4 py-2">{c.competitive_density.toFixed(0)}</td>
                  <td className="px-4 py-2">{c.content_quality_avg.toFixed(0)}</td>
                  <td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs capitalize ${directionClass(c.direction)}`}>{c.direction}</span></td>
                  <td className="px-4 py-2">{c.emerging_niche ? 'Yes' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'competitive' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {competitive.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between">
                <h3 className="font-medium text-slate-900">{c.competitor}</h3>
                {c.promotion_active && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Promo active</span>}
              </div>
              <p className="text-sm text-slate-500">{c.category}</p>
              <dl className="mt-3 space-y-1 text-sm text-slate-600">
                <div className="flex justify-between"><dt>Listing Quality</dt><dd>{c.listing_quality.toFixed(0)}</dd></div>
                <div className="flex justify-between"><dt>Content</dt><dd>{c.content_completeness.toFixed(0)}%</dd></div>
                <div className="flex justify-between"><dt>Visual</dt><dd>{c.visual_quality.toFixed(0)}</dd></div>
                <div className="flex justify-between"><dt>Position</dt><dd className="capitalize">{c.pricing_position ?? '—'}</dd></div>
                <div className="flex justify-between"><dt>Reviews</dt><dd>{c.review_count.toLocaleString()} ({c.rating_avg.toFixed(1)}★)</dd></div>
              </dl>
            </div>
          ))}
        </div>
      )}

      {tab === 'portfolio' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {portfolio.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex justify-between items-start">
                <h3 className="font-medium text-slate-900">{p.title}</h3>
                <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${actionClass(p.action)}`}>{p.action.replace(/_/g, ' ')}</span>
              </div>
              {p.rationale && <p className="mt-2 text-sm text-slate-600">{p.rationale}</p>}
              <p className="mt-2 text-xs text-slate-500">Priority: {p.priority_score.toFixed(0)}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <button type="button" onClick={generateRecs} disabled={busy}
            className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50">
            Generate AI Recommendations
          </button>
          {recommendations.map((rec) => (
            <div key={rec.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <span className="text-xs font-medium text-rose-600">#{rec.priority_rank} · {rec.type.replace(/_/g, ' ')}</span>
                  <h3 className="font-medium text-slate-900">{rec.title}</h3>
                  {rec.description && <p className="mt-1 text-sm text-slate-600">{rec.description}</p>}
                  {rec.evidence && <p className="mt-1 text-xs text-slate-500">{rec.evidence}</p>}
                </div>
                <div className="text-right text-sm">
                  <p className="font-medium">{(rec.confidence_score * 100).toFixed(0)}% confidence</p>
                  {rec.expected_impact && <p className="text-slate-500">{rec.expected_impact}</p>}
                  {rec.requires_approval && <p className="text-amber-600 text-xs">Requires approval</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'scenarios' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {[
              { type: 'category_growth', label: 'Category Growth' },
              { type: 'competitor_price_change', label: 'Competitor Price Cut' },
              { type: 'promotion_season', label: 'Prime Day Season' },
              { type: 'new_product_launch', label: 'New Product Launch' },
            ].map((s) => (
              <button key={s.type} type="button" disabled={busy} onClick={() => runScenario(s.type, s.label)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50">
                Simulate: {s.label}
              </button>
            ))}
          </div>
          {scenarios.map((sc) => (
            <div key={sc.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{sc.name}</h3>
              <p className="mt-1 text-sm text-slate-600">{sc.outcome}</p>
              <div className="mt-2 flex gap-4 text-xs text-slate-500">
                <span>Revenue: {sc.revenue_impact != null && sc.revenue_impact > 0 ? '+' : ''}{sc.revenue_impact?.toFixed(0)}%</span>
                <span>Profit: {sc.profit_impact != null && sc.profit_impact > 0 ? '+' : ''}{sc.profit_impact?.toFixed(0)}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
